import React, { RefObject, useId } from 'react';
import type { DependencyEdgeType, ProjectTask } from '../../types';
import { ganttDependencyConnectorPath } from '../../utils/ganttDependencyPaths';

const EDGE_OPTIONS: DependencyEdgeType[] = ['FS', 'SS', 'FF', 'SF'];

export function anchorsForEdgeType(type: DependencyEdgeType): { pred: 'start' | 'finish'; succ: 'start' | 'finish' } {
  switch (type) {
    case 'SS':
      return { pred: 'start', succ: 'start' };
    case 'FF':
      return { pred: 'finish', succ: 'finish' };
    case 'SF':
      return { pred: 'start', succ: 'finish' };
    default:
      return { pred: 'finish', succ: 'start' };
  }
}

/** Classic link names (matching common PM tooling). */
function dependencyLinkLabel(type: DependencyEdgeType): string {
  switch (type) {
    case 'FS':
      return 'Finish-to-start';
    case 'SS':
      return 'Start-to-start';
    case 'FF':
      return 'Finish-to-finish';
    case 'SF':
      return 'Start-to-finish';
    default:
      return '';
  }
}

/** PMI-style constraint wording (tie-out dates + lag to this relationship). */
function linkConstraintExplainer(type: DependencyEdgeType): string {
  switch (type) {
    case 'SS':
      return 'The successor cannot start until the predecessor starts.';
    case 'FF':
      return 'The successor cannot finish until the predecessor finishes.';
    case 'SF':
      return 'The successor cannot finish until the predecessor starts.';
    default:
      return 'The successor cannot start until the predecessor finishes.';
  }
}

const MINI_INSET = 5;

type MiniRect = { x: number; y: number; w: number; h: number };

/** Schematic bar layout: FS / SS avoid misleading overlap by default; FF aligns finishes; SF reads start→finish. FS + negative lag shows overlap (lead). */
function layoutMiniBars(linkType: DependencyEdgeType, lagBusinessDays: number): { pred: MiniRect; succ: MiniRect } {
  const VB_W = 360;
  const rightM = 18;
  const leftM = 22;
  const barH = 15;
  const predY = 38;
  const succY = 82;
  const anchorGapFs = 12;

  switch (linkType) {
    case 'FS': {
      const predW = 118;
      const predX = leftM;
      const pEndX = predX + predW - MINI_INSET;
      let sStartTarget: number;
      if (!Number.isFinite(lagBusinessDays) || lagBusinessDays >= 0) {
        sStartTarget = pEndX + anchorGapFs;
      } else {
        const overlap = Math.min(44, 18 + Math.min(52, Math.abs(lagBusinessDays) * 3));
        sStartTarget = pEndX - overlap;
      }
      const succX = sStartTarget - MINI_INSET;
      const succW = Math.max(96, VB_W - rightM - succX);
      return {
        pred: { x: predX, y: predY, w: predW, h: barH },
        succ: { x: succX, y: succY, w: succW, h: barH },
      };
    }
    case 'SS': {
      const predX = leftM;
      const predW = 102;
      const succX = predX;
      const succW = VB_W - rightM - succX;
      return {
        pred: { x: predX, y: predY, w: predW, h: barH },
        succ: { x: succX, y: succY, w: succW, h: barH },
      };
    }
    case 'FF': {
      const sharedEndX = VB_W - rightM - MINI_INSET;
      const succW = 168;
      const succX = Math.max(leftM, sharedEndX - succW + MINI_INSET);
      const predW = 76;
      const predX = Math.max(leftM, sharedEndX - predW + MINI_INSET);
      return {
        pred: { x: predX, y: predY, w: predW, h: barH },
        succ: { x: succX, y: succY, w: succW, h: barH },
      };
    }
    case 'SF': {
      const predX = leftM;
      const predW = 96;
      const pStartX = predX + MINI_INSET;
      const succW = 152;
      const sEndMin = pStartX + 56 + MINI_INSET;
      const sEndTarget = Math.max(sEndMin, VB_W - rightM - MINI_INSET);
      const succX = sEndTarget - succW + MINI_INSET;
      return {
        pred: { x: predX, y: predY, w: predW, h: barH },
        succ: { x: Math.max(leftM - 6, succX), y: succY, w: succW, h: barH },
      };
    }
    default:
      return layoutMiniBars('FS', lagBusinessDays);
  }
}

/** Lightweight schematic bars + Bézier arrows (coordinate space matches viewBox). */
function DependencyMiniDiagram({
  linkType,
  lagBusinessDays,
  zeroLagHighlight,
}: {
  linkType: DependencyEdgeType;
  lagBusinessDays: number;
  zeroLagHighlight?: boolean;
}) {
  const markerId = useId().replace(/:/g, '');
  const VB_W = 360;
  const VB_H = 128;
  const { pred, succ } = layoutMiniBars(linkType, lagBusinessDays);
  const py = pred.y + pred.h / 2;
  const sy = succ.y + succ.h / 2;
  const pStart = pred.x + MINI_INSET;
  const pEnd = pred.x + pred.w - MINI_INSET;
  const sStart = succ.x + MINI_INSET;
  const sEnd = succ.x + succ.w - MINI_INSET;

  let mx1 = pEnd;
  let my1 = py;
  let mx2 = sStart;
  let my2 = sy;
  switch (linkType) {
    case 'SS':
      mx1 = pStart;
      my1 = py;
      mx2 = sStart;
      my2 = sy;
      break;
    case 'FF':
      mx1 = pEnd;
      my1 = py;
      mx2 = sEnd;
      my2 = sy;
      break;
    case 'SF':
      mx1 = pStart;
      my1 = py;
      mx2 = sEnd;
      my2 = sy;
      break;
    default:
      break;
  }

  const d = ganttDependencyConnectorPath(mx1, my1, mx2, my2, linkType);
  const anchors = anchorsForEdgeType(linkType);
  const linkStroke = zeroLagHighlight ? 'rgb(13 148 136)' : 'rgb(51 65 85)';

  return (
    <svg
      className="pm-gantt-lag-mini-svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      height={VB_H}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${linkType}: dependency from predecessor ${anchors.pred} to successor ${anchors.succ}`}
    >
      <defs>
        <marker
          id={markerId}
          markerWidth={9}
          markerHeight={9}
          refX={7}
          refY={4.5}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0 0 L9 4.5 L0 9 Z" fill={linkStroke} />
        </marker>
      </defs>

      {/* Subtle lanes */}
      <rect x={12} y={34} width={VB_W - 24} height={23} rx={6} fill="rgba(148,163,184,0.09)" stroke="rgba(148,163,184,0.25)" strokeWidth={1} />
      <rect x={12} y={78} width={VB_W - 24} height={23} rx={6} fill="rgba(148,163,184,0.09)" stroke="rgba(148,163,184,0.25)" strokeWidth={1} />

      <rect x={pred.x} y={pred.y} width={pred.w} height={pred.h} rx={5} fill="rgb(13 148 136)" opacity={0.92} />
      <rect x={succ.x} y={succ.y} width={succ.w} height={succ.h} rx={5} fill="rgb(79 70 229)" opacity={0.88} />

      {/* Anchor knobs */}
      <circle cx={pStart} cy={py} r={4} fill="rgba(255,255,255,0.92)" stroke="rgb(15 118 110)" strokeWidth={1} />
      <circle cx={pEnd} cy={py} r={4} fill="rgba(255,255,255,0.92)" stroke="rgb(15 118 110)" strokeWidth={1} />
      <circle cx={sStart} cy={sy} r={4} fill="rgba(255,255,255,0.92)" stroke="rgb(79 70 229)" strokeWidth={1} />
      <circle cx={sEnd} cy={sy} r={4} fill="rgba(255,255,255,0.92)" stroke="rgb(79 70 229)" strokeWidth={1} />

      <path
        d={d}
        fill="none"
        stroke={linkStroke}
        strokeWidth={zeroLagHighlight ? 2.25 : 2}
        strokeDasharray={zeroLagHighlight ? '6 3' : undefined}
        markerEnd={`url(#${markerId})`}
      />
    </svg>
  );
}

export interface GanttLagPopoverState {
  successorId: string;
  predecessorId: string;
  lagDraft: string;
  linkType: DependencyEdgeType;
}

export interface GanttLagPopoverProps {
  state: GanttLagPopoverState;
  popoverRef: RefObject<HTMLDivElement>;
  predecessor: ProjectTask | undefined;
  successor: ProjectTask | undefined;
  onLinkTypeChange: (next: DependencyEdgeType) => void;
  onLagDraftChange: (draft: string) => void;
  onApply: () => void;
  onDismiss: () => void;
  onRemove?: () => void;
  allowEditLag: boolean;
  allowRemove: boolean;
}

const GanttLagPopover: React.FC<GanttLagPopoverProps> = ({
  state,
  popoverRef,
  predecessor,
  successor,
  onLinkTypeChange,
  onLagDraftChange,
  onApply,
  onDismiss,
  onRemove,
  allowEditLag,
  allowRemove,
}) => {
  const lagNum = parseInt(state.lagDraft, 10);
  const lagValid = !Number.isNaN(lagNum);
  const anchors = anchorsForEdgeType(state.linkType);

  const predTitle = predecessor?.title?.trim() || 'Predecessor';
  const succTitle = successor?.title?.trim() || 'Successor';

  return (
    <div
      className="pm-gantt-lag-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        ref={popoverRef}
        className="pm-gantt-lag-popover pm-gantt-lag-popover--modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit dependency lag"
      >
        <div className="pm-gantt-lag-popover-title">Dependency and lag</div>

        <div className="pm-gantt-lag-mini" aria-describedby="pm-gantt-lag-link-describe">
          <p className="pm-gantt-lag-mini-taskname pm-gantt-lag-mini-taskname--pred" title={predTitle}>
            {predTitle}
          </p>
          <DependencyMiniDiagram
            linkType={state.linkType}
            lagBusinessDays={lagValid ? lagNum : 0}
            zeroLagHighlight={lagValid && lagNum === 0}
          />
          <p className="pm-gantt-lag-mini-taskname pm-gantt-lag-mini-taskname--succ" title={succTitle}>
            {succTitle}
          </p>
          <div className="pm-gantt-lag-mini-caption-block" id="pm-gantt-lag-link-describe">
            <div className="pm-gantt-lag-mini-type-badge-line">
              <span className="pm-gantt-lag-mini-badge">{state.linkType}</span>
              <span className="pm-gantt-lag-mini-label">{dependencyLinkLabel(state.linkType)}</span>
            </div>
            <p className="pm-gantt-lag-mini-anchors">
              Predecessor <strong>{anchors.pred === 'start' ? 'Start' : 'Finish'}</strong>
              {' → '}
              Successor <strong>{anchors.succ === 'start' ? 'Start' : 'Finish'}</strong>
            </p>
            <p className="pm-gantt-lag-mini-explainer">{linkConstraintExplainer(state.linkType)}</p>
            {lagValid && lagNum === 0 && (
              <p className="pm-gantt-lag-mini-same-day">Same-day weekday offset: 0 business days between anchors.</p>
            )}
          </div>
        </div>

        <div className="pm-gantt-lag-popover-type-row">
          <span className="pm-gantt-lag-popover-label">Link type</span>
          <div className="pm-gantt-lag-type-toggle" role="group" aria-label="Dependency link type">
            {EDGE_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                className={`pm-gantt-lag-type-btn ${state.linkType === t ? 'is-on' : ''}`}
                onClick={() => onLinkTypeChange(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <p className="pm-gantt-lag-popover-hint">
          Weekday lag (Mon–Fri). Negative values mean overlap / lead between these anchors — not calendar-day distance.
        </p>

        {allowEditLag && (
          <label className="pm-gantt-lag-popover-field">
            <span className="pm-gantt-lag-popover-label">Lag (business days)</span>
            <input
              type="number"
              className="pm-gantt-lag-popover-input"
              step={1}
              min={-500}
              max={500}
              value={state.lagDraft}
              onChange={(e) => onLagDraftChange(e.target.value)}
              aria-label="Lag in business days"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onApply();
                }
              }}
              autoFocus
            />
          </label>
        )}

        {allowEditLag && (
          <div className="pm-gantt-lag-popover-actions">
            <button type="button" className="pm-btn pm-btn-sm pm-btn-primary" onClick={onApply} disabled={!lagValid}>
              Apply
            </button>
            <button type="button" className="pm-btn pm-btn-sm pm-btn-ghost" onClick={onDismiss}>
              Cancel
            </button>
          </div>
        )}

        {allowRemove && onRemove && (
          <button type="button" className="pm-gantt-lag-popover-remove" onClick={onRemove}>
            Remove dependency
          </button>
        )}
      </div>
    </div>
  );
};

export default GanttLagPopover;
